import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Printer, Edit, Trash2, Clock, Plus, X, History, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import OfficialPrescription from './OfficialPrescription';
import PrintHistoryModal from './PrintHistoryModal';
import { printPrescriptions } from '@/services/printService';

export default function PatientPrescriptionsModal({ 
  open, 
  onClose, 
  patient, 
  onEdit, 
  onDelete,
  onNewPrescription,
  onRefresh 
}) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
  const [activePrescriptionId, setActivePrescriptionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(true); // Estado para QR Code
  const [canGenerateQRCode, setCanGenerateQRCode] = useState(false); // Permissão de QR Code

  useEffect(() => {
    if (open && patient) {
      fetchPrescriptions();
      checkQRCodePermission();
    }
  }, [open, patient]);

  // Verificar permissão para gerar QR Code
  const checkQRCodePermission = async () => {
    try {
      const response = await axiosInstance.get('/auth/can-generate-qrcode');
      setCanGenerateQRCode(response.data.can_generate);
      // Se não tem permissão, desabilita QR Code por padrão
      if (!response.data.can_generate) {
        setShowQRCode(false);
      }
    } catch (error) {
      console.error('Erro ao verificar permissão de QR Code:', error);
      setCanGenerateQRCode(false);
      setShowQRCode(false);
    }
  };

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/prescriptions/patient/${patient.id}/all`);
      const prescriptionsList = Array.isArray(response.data) ? response.data : [response.data];
      setPrescriptions(prescriptionsList);
      if (prescriptionsList.length > 0) {
        setActivePrescriptionId(prescriptionsList[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar receitas:', error);
      toast.error('Erro ao carregar receitas');
      setLoading(false);
    }
  };

  const activePrescription = prescriptions.find(p => p.id === activePrescriptionId);

  // Função para registrar impressão e atualizar datas
  const registerPrint = async (prescriptionIds) => {
    try {
      const promises = prescriptionIds.map(id => 
        axiosInstance.post(`/prescriptions/${id}/print`)
      );
      const results = await Promise.all(promises);
      
      // Atualizar as prescrições locais com as novas datas
      setPrescriptions(prev => prev.map(p => {
        const result = results.find(r => r.data.prescription?.id === p.id);
        if (result) {
          return {
            ...p,
            data_impressao: result.data.data_impressao,
            data_validade: result.data.data_validade
          };
        }
        return p;
      }));
      
      return true;
    } catch (error) {
      console.error('Erro ao registrar impressão:', error);
      return false;
    }
  };

  const handlePrintSelected = async () => {
    if (selectedPrescriptions.length === 0) {
      toast.error('Selecione pelo menos uma receita para imprimir');
      return;
    }
    
    // Registrar impressão e atualizar datas
    await registerPrint(selectedPrescriptions);
    
    // Preparar itens para impressão
    const items = prescriptions
      .filter(p => selectedPrescriptions.includes(p.id))
      .map(p => ({ patient, prescription: p }));
    
    printPrescriptions(items, { showQRCode });
    if (onRefresh) onRefresh();
  };

  const handlePrintSingle = async () => {
    setSelectedPrescriptions([activePrescriptionId]);
    
    // Registrar impressão e atualizar datas
    await registerPrint([activePrescriptionId]);
    
    const prescription = prescriptions.find(p => p.id === activePrescriptionId);
    if (prescription) {
      printPrescriptions([{ patient, prescription }], { showQRCode });
    }
    if (onRefresh) onRefresh();
  };

  const togglePrescriptionSelection = (prescriptionId) => {
    setSelectedPrescriptions(prev => {
      if (prev.includes(prescriptionId)) {
        return prev.filter(id => id !== prescriptionId);
      } else {
        return [...prev, prescriptionId];
      }
    });
  };

  const handleDeletePrescription = async () => {
    if (!activePrescription) return;
    
    if (window.confirm(`Deseja realmente deletar esta receita?`)) {
      try {
        await axiosInstance.delete(`/prescriptions/${activePrescription.id}`);
        toast.success('Receita deletada com sucesso');
        
        // Remove from local state
        const updatedPrescriptions = prescriptions.filter(p => p.id !== activePrescription.id);
        setPrescriptions(updatedPrescriptions);
        
        // If no more prescriptions, close modal
        if (updatedPrescriptions.length === 0) {
          onClose();
          if (onRefresh) onRefresh();
        } else {
          // Set next active prescription
          setActivePrescriptionId(updatedPrescriptions[0].id);
        }
      } catch (error) {
        console.error('Erro ao deletar receita:', error);
        toast.error('Erro ao deletar receita');
      }
    }
  };

  const getUpdateInfo = (prescription) => {
    if (prescription.updated_at || prescription.data_atualizacao) {
      const updateDate = new Date(prescription.updated_at || prescription.data_atualizacao);
      return updateDate.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Receitas Médicas - {patient?.nome}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>
                {prescriptions.length} receita(s) encontrada(s)
              </span>
              {activePrescription && getUpdateInfo(activePrescription) && (
                <Badge variant="outline" className="ml-4 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  Atualizada em: {getUpdateInfo(activePrescription)}
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando receitas...
            </div>
          ) : prescriptions.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-4">Nenhuma receita encontrada</p>
              {onNewPrescription && (
                <Button onClick={onNewPrescription} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeira Receita
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Tabs for multiple prescriptions */}
              <Tabs value={activePrescriptionId} onValueChange={setActivePrescriptionId}>
                <div className="flex items-center justify-between mb-4 gap-2">
                  {/* Botões de seleção rápida */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPrescriptions(prescriptions.map(p => p.id))}
                      className="text-xs"
                      data-testid="select-all-prescriptions"
                    >
                      Selecionar Todas
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPrescriptions([])}
                      className="text-xs"
                      data-testid="deselect-all-prescriptions"
                    >
                      Desselecionar
                    </Button>
                  </div>
                  
                  <TabsList className="flex-1 overflow-x-auto">
                    {prescriptions.map((prescription, index) => (
                      <TabsTrigger 
                        key={prescription.id} 
                        value={prescription.id}
                        className={`relative ${selectedPrescriptions.includes(prescription.id) ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                        data-testid={`prescription-tab-${index}`}
                      >
                        <Checkbox
                          checked={selectedPrescriptions.includes(prescription.id)}
                          onCheckedChange={() => togglePrescriptionSelection(prescription.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mr-2"
                          data-testid={`prescription-checkbox-${index}`}
                        />
                        {prescription.name ? prescription.name : `Receita ${index + 1}`}
                        {prescription.uso && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {prescription.uso}
                          </Badge>
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {/* Indicador visual de receitas selecionadas */}
                {selectedPrescriptions.length > 0 && (
                  <div className="mb-4 p-2 bg-primary/10 rounded-lg text-sm text-primary font-medium flex items-center justify-between">
                    <span>
                      {selectedPrescriptions.length} receita(s) selecionada(s) para impressão
                    </span>
                    <Badge variant="default" className="bg-primary">
                      {selectedPrescriptions.length}/{prescriptions.length}
                    </Badge>
                  </div>
                )}

                {prescriptions.map((prescription) => (
                  <TabsContent key={prescription.id} value={prescription.id}>
                    <div className="no-print">
                      <OfficialPrescription patient={patient} prescription={prescription} />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Action Buttons */}
              <div className="flex flex-col gap-4 mt-6 border-t pt-4">
                {/* Toggle QR Code - Só aparece se usuário tem permissão */}
                {canGenerateQRCode && (
                  <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-gray-600" />
                      <Label htmlFor="qr-toggle" className="text-sm text-gray-700">
                        Incluir QR Code de validação na impressão
                      </Label>
                    </div>
                    <Switch
                      id="qr-toggle"
                      checked={showQRCode}
                      onCheckedChange={setShowQRCode}
                    />
                  </div>
                )}
                
                {/* Print Buttons */}
                <div className="flex gap-3">
                {selectedPrescriptions.length > 0 ? (
                  <>
                    <Button 
                      onClick={handlePrintSelected} 
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir Selecionadas ({selectedPrescriptions.length})
                    </Button>
                    <Button
                      onClick={() => setSelectedPrescriptions([])}
                      variant="outline"
                      className="rounded-full h-12"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Limpar Seleção
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      onClick={handlePrintSingle} 
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir Esta
                    </Button>
                    {onNewPrescription && (
                      <Button 
                        onClick={onNewPrescription} 
                        variant="outline" 
                        className="flex-1 rounded-full h-12 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Nova Receita
                      </Button>
                    )}
                    {onEdit && (
                      <Button 
                        onClick={() => onEdit(activePrescription)} 
                        variant="outline" 
                        className="flex-1 rounded-full h-12"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    )}
                    <Button 
                      onClick={handleDeletePrescription} 
                      variant="destructive" 
                      className="flex-1 rounded-full h-12"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Deletar
                    </Button>
                    <Button
                      onClick={() => setShowHistoryModal(true)}
                      variant="outline"
                      className="rounded-full h-12 text-purple-600 border-purple-200 hover:bg-purple-50"
                      title="Ver histórico de renovações"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Histórico
                    </Button>
                  </>
                )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Print History Modal */}
      <PrintHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        prescriptionId={activePrescriptionId}
        prescriptionName={activePrescription?.name || `Receita - ${activePrescription?.uso || ''}`}
      />
    </>
  );
}
